import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import {
  alias,
  capture,
  getDistinctId,
  identify,
  initPostHog,
  isPostHogReady,
  reset,
} from '../lib/posthog';
import { markSignupTracked, signupAlreadyTracked } from '../lib/signup-tracking';

const FIVE_MINUTES_MS = 5 * 60 * 1000;

/**
 * One `$pageview` per page change (W.4.109). posthog-js runs with
 * `capture_pageview: 'history_change'`: it records the first page and every
 * change of path itself. This adds the one change it does not see — a new
 * query on the same path (`/documents?sharing=published`). Recording path
 * changes here as well counted each of them twice.
 */
function PageViewTracker() {
  const location = useLocation();
  const lastRef = useRef<{ pathname: string; search: string } | null>(null);
  useEffect(() => {
    const previous = lastRef.current;
    lastRef.current = { pathname: location.pathname, search: location.search };
    // The first page and any change of path are the SDK's.
    if (!previous || previous.pathname !== location.pathname) return;
    if (previous.search === location.search || !isPostHogReady()) return;
    capture('$pageview', {
      $current_url: window.location.origin + location.pathname + location.search,
      $pathname: location.pathname,
    });
  }, [location.pathname, location.search]);
  return null;
}

function ClerkIdentify() {
  const { user, isLoaded } = useUser();
  const identifiedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isPostHogReady() || !isLoaded) return;
    if (!user) {
      if (identifiedRef.current) {
        reset();
        identifiedRef.current = null;
      }
      return;
    }
    if (identifiedRef.current === user.id) return;

    const anonId = getDistinctId();
    identify(user.id, {
      email: user.primaryEmailAddress?.emailAddress,
      name: user.fullName,
      created_at: user.createdAt?.toISOString(),
    });

    if (anonId && !anonId.startsWith('user_')) {
      alias(user.id, anonId);
    }

    // Treat users created in the last 5 minutes as a fresh signup — once: the
    // landing sends a new account on to the app, and both hosts load this
    // (W.4.109). The cookie is shared by the landing host and the app host.
    const createdAt = user.createdAt ? new Date(user.createdAt).getTime() : 0;
    if (
      createdAt &&
      Date.now() - createdAt < FIVE_MINUTES_MS &&
      !signupAlreadyTracked(user.id)
    ) {
      let storedAnonId: string | null = null;
      try {
        storedAnonId = localStorage.getItem('ph_pre_signup_anon_id');
      } catch {
        /* ignore */
      }
      if (storedAnonId && storedAnonId !== anonId) {
        alias(user.id, storedAnonId);
      }
      try {
        localStorage.removeItem('ph_pre_signup_anon_id');
      } catch {
        /* ignore */
      }
      capture('signup_completed', {
        signup_method: user.primaryEmailAddress ? 'email' : 'oauth',
        source: window.location.pathname,
        pre_signup_anonymous_id: storedAnonId || undefined,
      });
      markSignupTracked(user.id);
    }

    identifiedRef.current = user.id;
  }, [user, isLoaded]);

  return null;
}

export function PostHogAnalytics({ hasClerk }: { hasClerk: boolean }) {
  useEffect(() => {
    initPostHog();
  }, []);

  return (
    <>
      <PageViewTracker />
      {hasClerk && <ClerkIdentify />}
    </>
  );
}
